import { useState } from "react";
import { Link } from "react-router-dom";
import { useInfiniteQuery } from "@tanstack/react-query";
import { api, fmtTime, type Issue } from "../api";
import { useGroups, useLogGroups } from "../hooks";
import { useSession } from "../session";

const STATUSES = ["open", "acknowledged", "retrying", "dead_lettered", "resolved", "completed"];

export function StatusBadge({ status, regressed }: { status: string; regressed?: boolean }) {
  return (
    <span className={`badge st-${status}`}>
      {status.replace("_", " ")}
      {regressed ? " · regressed" : ""}
    </span>
  );
}

export function IssuesPage() {
  const { tenant } = useSession();
  const groups = useGroups();
  const logGroups = useLogGroups();
  const [filters, setFilters] = useState({ group: "", log_group: "", status: ["open", "acknowledged", "retrying", "dead_lettered"], kind: "", q: "" });
  const query = useInfiniteQuery({
    queryKey: ["issues", tenant, filters],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      api<{ issues: Issue[]; next_cursor: string | null }>("/v1/issues", {
        query: { ...filters, log_group: filters.group ? filters.log_group : "", limit: 50, cursor: pageParam ?? undefined },
      }),
    getNextPageParam: (last) => last.next_cursor,
  });
  const issues = query.data?.pages.flatMap((p) => p.issues) ?? [];
  const lgName = (id: string) => logGroups.data?.find((l) => l.id === id);

  function toggleStatus(s: string) {
    setFilters((f) => ({ ...f, status: f.status.includes(s) ? f.status.filter((x) => x !== s) : [...f.status, s] }));
  }

  return (
    <section aria-labelledby="issues-title">
      <h1 id="issues-title">Issues</h1>
      <form className="row" onSubmit={(e) => e.preventDefault()}>
        <label>
          Group
          <select value={filters.group} onChange={(e) => setFilters({ ...filters, group: e.target.value, log_group: "" })}>
            <option value="">all</option>
            {groups.data?.map((g) => <option key={g.id} value={g.name}>{g.name}</option>)}
          </select>
        </label>
        <label>
          Log group
          <select value={filters.log_group} disabled={!filters.group} onChange={(e) => setFilters({ ...filters, log_group: e.target.value })}>
            <option value="">all</option>
            {logGroups.data?.filter((l) => l.group_name === filters.group).map((l) => <option key={l.id} value={l.name}>{l.name}</option>)}
          </select>
        </label>
        <label>
          Kind
          <select value={filters.kind} onChange={(e) => setFilters({ ...filters, kind: e.target.value })}>
            <option value="">all</option>
            <option value="exception">exceptions</option>
            <option value="processing">processing errors</option>
          </select>
        </label>
        <label className="grow">
          Search
          <input value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} placeholder="title or message" />
        </label>
      </form>
      <fieldset className="row statuses">
        <legend>Status</legend>
        {STATUSES.map((s) => (
          <label key={s} className="inline">
            <input type="checkbox" checked={filters.status.includes(s)} onChange={() => toggleStatus(s)} /> {s.replace("_", " ")}
          </label>
        ))}
      </fieldset>
      {query.isError && <p role="alert" className="error">{(query.error as Error).message}</p>}
      <table className="grid">
        <caption className="sr-only">Error issues</caption>
        <thead>
          <tr><th scope="col">Status</th><th scope="col">Issue</th><th scope="col">Where</th><th scope="col">Count</th><th scope="col">Attempts</th><th scope="col">Last seen</th></tr>
        </thead>
        <tbody>
          {issues.map((i) => {
            const lg = lgName(i.log_group_id);
            return (
              <tr key={i.id}>
                <td><StatusBadge status={i.status} regressed={i.regressed} /></td>
                <td>
                  <Link to={`/issues/${i.id}`}>{i.title}</Link>
                  <div className="muted small">{i.kind}{i.component ? ` · ${i.component}` : ""}</div>
                </td>
                <td className="small">{lg ? `${lg.group_name} / ${lg.name}` : "–"}</td>
                <td>{i.count}</td>
                <td>{i.kind === "processing" ? `${i.attempts}${i.max_attempts ? ` / ${i.max_attempts}` : ""}` : "–"}</td>
                <td className="small">{fmtTime(i.last_seen)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {!query.isLoading && !issues.length && <p className="muted">No issues match.</p>}
      {query.hasNextPage && <button type="button" onClick={() => query.fetchNextPage()}>Load more</button>}
    </section>
  );
}
